import React, { Component } from "react";
import PropTypes from "prop-types";
import moment from "moment";
import "./ChatWidget.scss";
import "../TimeCounter/TimeCounter.scss";
import Scrollbars from "components/Scrollbars/Scrollbars";
import { getUsername } from "utils/Common";
import MessageCard from "components/MessageCard/MessageCard";
import TimeCounter from "components/TimeCounter/TimeCounter";
import { debounce } from "lodash";
import _ from "lodash";
import { v4 as uuid } from "uuid";
import { connect } from "react-redux";
import {
  addNewMessage,
  setClosedConversationMessages,
  setTransferredConversationMessages,
} from "actions/conversationActions";
import { uepClient } from "uepClient";
import { ConversationStatus } from "constants/ChatListingConstants";
import ChatExpirationService from "services/ChatExpirationService";
import {
  ChatExpirationEvents,
  MessageStatus,
} from "constants/ChatStatusConstants";
import { MessageSources } from "constants/MessageConstants";
import { AgentViewTypes } from "constants/ChatViewConstants";
import { getGroup } from "utils/Common";
import { MessageType, UserChannel } from "constants/UserConstants";
import ChatTextArea from "./ChatTextArea";
import SearchField from "./SearchField";
import Channels from "components/Channels/Channels";
import { getMessageTypeBasedOnFormat } from "utils/Helpers";

class ChatWidget extends Component {
  static propTypes = {
    className: PropTypes.string,
    conversation: PropTypes.object,
    onSendNewMessage: PropTypes.func,
    isActive: PropTypes.bool,
    agentView: PropTypes.oneOf([
      AgentViewTypes.DEFAULT,
      AgentViewTypes.TRANSFER,
    ]),
  };

  static defaultProps = {
    className: "",
    conversation: null,
    onSendNewMessage: () => { },
  };

  constructor() {
    super();
    this.agentGroup = getGroup();
    this.state = {
      isLoading: true,
      currentAgentId: getUsername(),
      showSearch: false,
      highlightedMessages: [],
      initialDurationTime: 0,
      isChatFreezed: false,
    };
    this.chatScrollRef = React.createRef();
    this.debouncedScrollToBottom = debounce(this.scrollChatWindowToEnd, 500);
  }

  componentDidMount() {
    this.getAndSetCurrentAgentId();
    this.handleMessagesForClosedOrTransferredConversation();
    this.calculateDurationTimeAndSet();
    this.chatExpirationSubscription = ChatExpirationService.listen().subscribe(
      this.onMessageFromChatExpiration
    );
  }

  componentWillUnmount() {
    this.chatExpirationSubscription.unsubscribe();
  }

  componentDidUpdate(prevProps) {
    this.handleNewMessages(prevProps.conversation.messages);
    if (
      prevProps.conversation.conversationId !==
      this.props.conversation.conversationId
    ) {
      this.handleMessagesForClosedOrTransferredConversation();
    }

    if (!prevProps.isActive && this.props.isActive) {
      this.debouncedScrollToBottom();
    }
  }

  onMessageFromChatExpiration = (message) => {
    if (
      message.event === ChatExpirationEvents.FREEZE &&
      message.conversation.conversationId ===
      this.props.conversation.conversationId
    ) {
      this.setState({
        isChatFreezed: true,
      });
    }
  };

  calculateDurationTimeAndSet = () => {
    const { messages } = this.props.conversation;
    if (!messages.length) return;
    const currentTime = moment();
    const firstMessageOfConversation = messages[0];
    const diff = moment(currentTime).diff(
      firstMessageOfConversation.messageTime
    );
    this.setState({
      initialDurationTime: diff,
    });
  };

  handleMessagesForClosedOrTransferredConversation = () => {
    if (this.props.conversation) {
      switch (this.props.conversation.status) {
        case ConversationStatus.CLOSED:
          return this.fetchMessagesForClosedConversation();
        case ConversationStatus.TRANSFERRED:
          return this.fetchMessagesForTransferredConversation();
        default:
          return;
      }
    }
  };

  fetchMessagesForTransferredConversation = () => {
    const { conversation } = this.props;
    const conversationId = conversation.conversationId;
    const { currentAgentId } = this.state;
    uepClient()
      .get(`/chat/conversation/${conversationId}/messages/${currentAgentId}`)
      .then((res) => {
        const messages = res.data;
        this.props.setTransferredConversationMessages(conversationId, messages);
      })
      .catch((err) => {
        console.error(
          `Failed to fetch messages of transferred conversation : ${conversationId}`,
          err
        );
      });
  };

  fetchMessagesForClosedConversation = () => {
    if (this.props.conversation.messages.length) {
      return;
    }
    const closedConversationId = this.props.activeConversationId;
    uepClient()
      .get(`chat/conversation/${closedConversationId}/messages`)
      .then((response) => {
        this.props.setClosedConversationMessages({
          conversationId: closedConversationId,
          messages: response.data,
        });
      })
      .catch((error) => {
        console.error(
          "Some Error Occured while fetching messages for closed conversation : ",
          error.message
        );
      });
  };

  handleNewMessages = (prevMessages) => {
    const { messages } = this.props.conversation;
    if (messages.length > prevMessages.length) {
      this.scrollChatWindowToEnd();
    }
  };

  getAndSetCurrentAgentId = () => {
    const agentId = getUsername();
    this.setState({
      currentAgentId: agentId,
    });
  };

  isHighlightedMessage = (message) => {
    const { highlightedMessages } = this.state;
    return highlightedMessages.some(
      (msg) => msg.messageId === message.messageId
    );
  };

  renderTransferCard = (message) => {
    const { conversation } = this.props;
    const chatTransferDetailsOfMessage = conversation.chatTransferDetails?.find(
      (cd) => cd.messageId === message.messageId
    );

    if (chatTransferDetailsOfMessage) {

      const agentFullName = chatTransferDetailsOfMessage.transferredByAgentFirstName && chatTransferDetailsOfMessage.transferredByAgentLastName ?
        `${chatTransferDetailsOfMessage.transferredByAgentFirstName} ${chatTransferDetailsOfMessage.transferredByAgentLastName}` :
        `${chatTransferDetailsOfMessage.transferredByAgentId}`
      return this.renderConversationDetail({
        conversationDate: conversation.conversationDate,
        text:
          "Transferred by " +
          (agentFullName || chatTransferDetailsOfMessage.transferredByAgentId),
        key: `${message.messageId}`,
      });
    }
    return "";
  };

  renderMessages = (messages) => {
    return messages.map((message) => {
      if (message.messageStatus === MessageStatus.TRANSFERRED) {
        return this.renderTransferCard(message);
      }
      return (
        <MessageCard
          key={message.messageId}
          message={message}
          messageSource={
            message.source === MessageSources.AGENT ||
              message.source === MessageSources.BOT
              ? "to"
              : "from"
          }
          highlighted={this.isHighlightedMessage(message)}
          isTransferred={this.isTransferredConversation()}
        />
      );
    });
  };

  isTransferredConversation = () => {
    const { conversation, activeConversationStatus } = this.props;
    return (
      conversation.chatTransferDetails?.length > 0 &&
      activeConversationStatus !== ConversationStatus.TRANSFERRED
    );
  };

  onSendNewLocationMessage = (locationMessage, suggestionInfo) => {
    const { conversation } = this.props;
    const { currentAgentId } = this.state;
    const newMessage = {
      messageId: uuid(),
      message: "",
      fileUrl: "",
      messageTime: moment().utc().toISOString(),
      phoneNumber: conversation.conversationMeta.phoneNumber,
      customerId: conversation.conversationMeta.customerId,
      source: "AGENT",
      conversationId: conversation.conversationId,
      agentId: currentAgentId,
      messageType: MessageType.LOCATION,
      latitude: locationMessage.latitude,
      longitude: locationMessage.longitude,
      locationName: locationMessage.locationName,
      address: locationMessage.address,
      channel: conversation.conversationMeta.channel,
      group: this.agentGroup,
      metaData: {
        suggestionInfo,
      },
    };
    this.props.addNewMessage(newMessage);
    this.props.onSendNewMessage(newMessage);
  };

  onSendNewTextMessage = (message, suggestionInfo) => {
    const { conversation } = this.props;
    const { currentAgentId } = this.state;
    const newMessage = {
      messageId: uuid(),
      message: message,
      fileUrl: "",
      messageTime: moment().utc().toISOString(),
      phoneNumber: conversation.conversationMeta.phoneNumber,
      customerId: conversation.conversationMeta.customerId,
      source: "AGENT",
      conversationId: conversation.conversationId,
      agentId: currentAgentId,
      messageType: MessageType.TEXT,
      channel: conversation.conversationMeta.channel,
      group: this.agentGroup,
      metaData: {
        suggestionInfo,
      },
    };
    this.props.addNewMessage(newMessage);
    this.props.onSendNewMessage(newMessage);
    ChatExpirationService.startWaitingForClientReply(conversation);
  };

  onSendNewAttachment = (attachment, suggestionInfo) => {
    const { conversation } = this.props;
    const { currentAgentId } = this.state;
    const newMessage = {
      messageId: uuid(),
      message: attachment.label,
      fileUrl: attachment.fileUrl,
      messageTime: moment().utc().toISOString(),
      phoneNumber: conversation.conversationMeta.phoneNumber,
      customerId: conversation.conversationMeta.customerId,
      source: "AGENT",
      conversationId: conversation.conversationId,
      channel: conversation.conversationMeta.channel,
      agentId: currentAgentId,
      messageType:
        this.channel === UserChannel.WHATSAPP
          ? MessageType.DOCUMENT
          : getMessageTypeBasedOnFormat(attachment.format),
      group: this.agentGroup,
      metaData: {
        suggestionInfo,
      },
    };
    this.props.addNewMessage(newMessage);
    this.props.onSendNewMessage(newMessage);
    ChatExpirationService.startWaitingForClientReply(conversation);
  };

  scrollChatWindowToEnd = () => {
    this.chatScrollRef.current.scrollToBottom();
  };

  onSearch = (highlightedMessages) => {
    this.setState({
      highlightedMessages,
    });
  };

  onShowSearch = (showSearch) => {
    this.setState({
      showSearch,
    });
  };

  getChatName = (conversation) => {
    return (
      conversation.conversationMeta.name ||
      conversation.conversationMeta.customerId
    );
  };

  renderMessagesFromOlderConversationOfSameClient = () => {
    const { olderConversations } = this.props;
    const olderConversationsToRender = [];
    if (olderConversations && olderConversations.length) {
      olderConversations.forEach((conversation) => {
        olderConversationsToRender.push(
          <React.Fragment key={conversation.conversationId}>
            {this.renderMessages(conversation.messages)}
            {this.renderConversationDetail({
              conversationDate: conversation.conversationDate,
              text:
                "Closed by " + (conversation.agentName || conversation.agentId),
            })}
          </React.Fragment>
        );
      });
    }
    return olderConversationsToRender;
  };

  renderConversationDetail = ({ conversationDate, text, key = "" }) => {
    return (
      <div key={key} className="conversation-detail">
        <div className="conversation-date-container">
          <div className="border-line" />
          <div className="conversation-date">
            <span className="date-text">
              {moment(conversationDate).format("MMMM Do YYYY")}
            </span>
          </div>
        </div>
        <div className="conversation-agent">{text}</div>
      </div>
    );
  };

  renderMessagesFromCurrentConversation = (conversation) => {
    const conversationFragmentsToRender = [];
    if (conversation.messages && conversation.messages.length > 0) {
      const messageGroups = _.groupBy(conversation.messages, (message) =>
        moment(message.messageTime, "YYYY-MM-DD").format("YYYY-MM-DD")
      );

      Object.keys(messageGroups).forEach((date) => {
        const messagesInGroup = messageGroups[date];
        conversationFragmentsToRender.push(
          <React.Fragment key={date}>
            {this.renderConversationDate({
              conversationDate: date,
            })}
            {this.renderMessages(messagesInGroup)}
          </React.Fragment>
        );
      });
    }
    return conversationFragmentsToRender;
  };

  renderConversationDate = ({ conversationDate, key = "" }) => {
    return (
      <div key={key} className="conversation-detail">
        <div className="conversation-date-container">
          <div className="border-line" />
          <div className="conversation-date">
            <span className="date-text">
              {moment(conversationDate).format("MMMM Do YYYY")}
            </span>
          </div>
        </div>
      </div>
    );
  };

  renderChatDuration = (conversation) => {
    if (
      conversation.conversationMeta.startTime &&
      conversation.conversationMeta.endTime
    ) {
      const startTime = moment(conversation.conversationMeta.startTime);
      const endTime = moment(conversation.conversationMeta.endTime);
      const conversationDuration = moment.duration(endTime.diff(startTime));
      return this.getFormattedDuration(conversationDuration);
    }
    if (conversation.status === "TRANSFERRED") {
      if (conversation.messages && conversation.messages.length > 0) {
        //filter out message objects with no message string
        const filteredMsgs = conversation.messages.filter(
          (msg) => msg.messageStatus !== MessageStatus.TRANSFERRED
        );
        const messageCount = filteredMsgs.length;
        const startTime = moment(filteredMsgs[0].messageTime);
        const endTime = moment(filteredMsgs[messageCount - 1].messageTime);
        const conversationDuration = moment.duration(endTime.diff(startTime));
        return this.getFormattedDuration(conversationDuration);
      }
    }
  };

  getFormattedDuration = (conversationDuration) => {
    const hours = conversationDuration.hours();
    const mins = conversationDuration.minutes();
    const seconds = conversationDuration.seconds();
    const hoursToDisplay = parseInt(hours) < 10 ? `0${hours}` : hours;
    const minsToDisplay = parseInt(mins) < 10 ? `0${mins}` : mins;
    const secondsToDisplay = parseInt(seconds) < 10 ? `0${seconds}` : seconds;
    const durationToDisplay = `${hoursToDisplay}:${minsToDisplay}:${secondsToDisplay}`;
    return durationToDisplay;
  };

  render() {
    const { className, conversation, isActive, agentView } = this.props;
    const { olderConversations } = this.props;
    const { showSearch, initialDurationTime, isChatFreezed } = this.state;
    const searchBoxCls = showSearch ? "show-search" : "";
    const displayCls = isActive ? "active" : "d-none";

    const isTransferringCls =
      isActive &&
        agentView === AgentViewTypes.TRANSFER &&
        conversation.status === ConversationStatus.OPEN
        ? "transferring"
        : "";

    return (
      <div
        className={`chat-widget ${className} ${displayCls} ${searchBoxCls} ${isTransferringCls}`}
        data-testid="chat-widget"
      >
        <div className="chat-messages-container">
          <div className="chat-header">
            <div className="comment-icon">
              <Channels
                channels={[conversation.conversationMeta.channel]}
                size={"2.0em"}
              />
            </div>
            <div className="name-and-search">
              <div className="chat-person-name">
                {this.getChatName(conversation)}
              </div>
              <SearchField
                conversation={conversation}
                olderConversations={olderConversations}
                onSearch={this.onSearch}
                onShowSearch={this.onShowSearch}
              />
            </div>
            {conversation.status === ConversationStatus.OPEN ? (
              <TimeCounter
                key={initialDurationTime}
                initialTime={initialDurationTime}
                className="timer"
              />
            ) : (
              <div className="time-counter">
                {this.renderChatDuration(conversation)}
              </div>
            )}
          </div>
          <div className="messages-list-container">
            <Scrollbars ref={this.chatScrollRef}>
              <div className="messages-list old">
                {this.renderMessagesFromOlderConversationOfSameClient(
                  conversation
                )}
              </div>
              <div className="messages-list new">
                {this.renderMessagesFromCurrentConversation(conversation)}
              </div>
              <div ref={this.messageEndRef} className="message-end" />
            </Scrollbars>
          </div>
        </div>
        {conversation.status === ConversationStatus.OPEN && (
          <ChatTextArea
            onSendNewAttachment={this.onSendNewAttachment}
            onSendNewTextMessage={this.onSendNewTextMessage}
            onSendNewLocationMessage={this.onSendNewLocationMessage}
            isChatFreezed={isChatFreezed}
            agentGroup={this.agentGroup}
            isActiveConversation={isActive}
          />
        )}
      </div>
    );
  }
  isSendDisabled = () => {
    const { message, isChatFreezed } = this.state;
    return (
      (message.trim().length === 0 && this.state.attachedFile === null) ||
      isChatFreezed
    );
  };
}

const mapStateToProps = (state) => ({
  activeConversationId: state.conversation.activeConversationId,
  activeConversationStatus: state.conversation.activeConversationStatus,
  attachedFile: state.conversation.attachedFile,
  agentView: state.agentView,
});

const mapDispatchToProps = {
  addNewMessage,
  setClosedConversationMessages,
  setTransferredConversationMessages,
};

export default connect(mapStateToProps, mapDispatchToProps)(React.memo(ChatWidget));
